import { Coord, Row, Direction, colIndex, indexToCol, COLS, ROWS, coordKey } from '../types/grid.js'
import { Ship, ShipType } from '../types/ship.js'
import { CellHit } from '../types/game.js'
import { getShipCells } from './placement.js'

export interface TorpedoCellResult {
  coord: Coord
  hit: boolean
  sunkShip: ShipType | null
}

export interface TorpedoResult {
  cells: TorpedoCellResult[]
}

/**
 * Compute the cells targeted by a torpedo.
 * GDD §4.2: If the second cell would fall outside the grid, only the first cell is attacked.
 * GDD-v3 §4: A second cell on terrain resolves like out-of-bounds (first cell only).
 */
export function getTorpedoCells(
  target: Coord,
  direction: Direction,
  blockedCells?: Set<string>,
): Coord[] {
  const cells: Coord[] = [target]

  let second: Coord | null = null
  if (direction === 'horizontal') {
    const nextColIdx = colIndex(target.col) + 1
    if (nextColIdx < COLS.length) {
      second = { col: indexToCol(nextColIdx), row: target.row }
    }
  } else {
    const nextRow = target.row + 1
    if (nextRow <= ROWS.length) {
      second = { col: target.col, row: nextRow as Row }
    }
  }

  if (second && !blockedCells?.has(coordKey(second))) {
    cells.push(second)
  }

  return cells
}

/**
 * Apply a torpedo action. Processes each cell independently.
 * GDD §7: Torpedo on already-revealed cells is allowed mechanically.
 */
export function applyTorpedo(
  target: Coord,
  direction: Direction,
  ships: Ship[],
  existingHits: CellHit[],
  blockedCells?: Set<string>,
): TorpedoResult {
  if (blockedCells?.has(coordKey(target))) {
    throw new Error(`Cell ${coordKey(target)} is a terrain cell and cannot be targeted`)
  }
  const cells = getTorpedoCells(target, direction, blockedCells)
  const hitKeys = new Set(existingHits.filter(h => h.hit).map(h => coordKey(h.coord)))
  const allHitKeys = new Set(existingHits.map(h => coordKey(h.coord)))

  // Build a map of coord → ship for quick lookup
  const cellToShip = new Map<string, Ship>()
  for (const ship of ships) {
    for (const c of getShipCells(ship)) {
      cellToShip.set(coordKey(c), ship)
    }
  }

  // Accumulate new hits as we process each cell
  const newHitKeys = new Set(hitKeys)
  const results: TorpedoCellResult[] = []

  for (const cell of cells) {
    const key = coordKey(cell)

    // If already targeted, still report the result but it's a "wasted" hit
    if (allHitKeys.has(key)) {
      const wasHit = hitKeys.has(key)
      results.push({ coord: cell, hit: wasHit, sunkShip: null })
      continue
    }

    const ship = cellToShip.get(key)
    const hit = ship !== undefined

    let sunkShip: ShipType | null = null
    if (ship && hit) {
      newHitKeys.add(key)
      const shipCells = getShipCells(ship)
      if (shipCells.every(c => newHitKeys.has(coordKey(c)))) {
        sunkShip = ship.type
      }
    }

    results.push({ coord: cell, hit, sunkShip })
  }

  return { cells: results }
}
